import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";

/**
 * Marketing primitives: the shared design system for every public page.
 *
 * Direction: warm editorial print, not dark cyber SaaS. A cream paper
 * canvas, near-black ink, one display serif (EB Garamond) used at normal
 * weight with *italic* emphasis words, Inter for everything else, and
 * section rhythm made from full-width rounded color panels (deep green,
 * near-black) rather than borders and glows. Color is reserved for
 * meaning (severity, state); decoration earns its place or goes.
 *
 * Tokens (Tailwind arbitrary values, kept here so every page agrees):
 *   paper   #FBF8F1   canvas
 *   ink     #17150F   text on paper
 *   line    #E4DFD3   hairlines on paper
 *   green   #1E4D38   brand panel + primary buttons
 *   mint    #E4EFE4   tinted secondary button fill
 *   coal    #141412   the dark panel
 */

export const TOKENS = {
  paper: "#FBF8F1",
  ink: "#17150F",
  line: "#E4DFD3",
  green: "#1E4D38",
  mint: "#E4EFE4",
  coal: "#141412",
};

// EB Garamond: a classic Garamond for display type. Chosen deliberately for
// an editorial, print-emulating brand; set at normal weight with one italic
// emphasis phrase per headline.
export const SERIF = { fontFamily: "'EB Garamond', Georgia, 'Times New Roman', serif" };

// ──────────────────────────────────────────────────────────────────
// Reveal — one gentle rise-and-fade as an element enters the viewport.
// Calm and singular: small distance, one easing, fires once. The global
// prefers-reduced-motion rule in index.css collapses it for those users.
// ──────────────────────────────────────────────────────────────────

export const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

export function Reveal({
  children,
  delay = 0,
  y = 18,
  className = "",
}: {
  children: React.ReactNode;
  delay?: number;
  y?: number;
  className?: string;
}) {
  // Motion here is applied as inline styles by JS, so the CSS
  // prefers-reduced-motion rule cannot stop it. Honor the preference
  // explicitly: render static, no initial offset, no fade.
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.55, delay, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Section — vertical rhythm + horizontal container
// ──────────────────────────────────────────────────────────────────

export function Section({
  id,
  children,
  className = "",
  container = "default",
  padding = "default",
}: {
  id?: string;
  children: React.ReactNode;
  className?: string;
  container?: "default" | "wide" | "narrow" | "none";
  padding?: "default" | "tight" | "hero";
}) {
  const containerClass = {
    default: "mx-auto max-w-6xl px-5 sm:px-8",
    wide:    "mx-auto max-w-7xl px-5 sm:px-8",
    narrow:  "mx-auto max-w-3xl px-5 sm:px-8",
    none:    "",
  }[container];
  const padClass = {
    default: "py-20 md:py-28",
    tight:   "py-12 md:py-16",
    hero:    "pt-28 pb-14 md:pt-32 md:pb-20",
  }[padding];

  return (
    <section id={id} className={`relative ${padClass} ${className}`}>
      <div className={containerClass}>{children}</div>
    </section>
  );
}

/**
 * Panel — a full-width rounded color block, the device this design uses
 * for rhythm. Sits inside a Section with container="none"; content goes
 * in its own container inside.
 */
export function Panel({
  tone = "green",
  className = "",
  children,
}: {
  tone?: "green" | "coal" | "white";
  className?: string;
  children: React.ReactNode;
}) {
  const toneClass = {
    green: "bg-[#1E4D38] text-[#F5F1E6]",
    coal:  "bg-[#141412] text-[#F5F1E6]",
    white: "bg-white text-[#17150F] border border-[#E4DFD3]",
  }[tone];
  return (
    <div className={`mx-3 rounded-[2rem] sm:mx-5 md:mx-8 ${toneClass} ${className}`}>
      {children}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Type — serif display with italic emphasis, sans supporting text
// ──────────────────────────────────────────────────────────────────

export function Eyebrow({
  children,
  className = "",
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`inline-flex items-center text-[11px] font-semibold uppercase tracking-[0.22em] opacity-60 ${className}`}
    >
      {children}
    </div>
  );
}

export function Headline({
  children,
  size = "section",
  className = "",
}: {
  children: React.ReactNode;
  size?: "hero" | "section" | "small";
  className?: string;
  gradient?: boolean;
}) {
  // Line-height never below 1.1: italic emphasis words with descenders
  // (y g j p q) clip at tighter leading. pb-1 reserves the descender space.
  const sizeClass = {
    hero:    "text-4xl md:text-5xl lg:text-6xl leading-[1.1] pb-1",
    section: "text-3xl md:text-[2.6rem] leading-[1.12] pb-1",
    small:   "text-2xl md:text-3xl leading-[1.15]",
  }[size];

  return (
    <h2 className={`${sizeClass} font-normal tracking-[-0.01em] ${className}`} style={SERIF}>
      {children}
    </h2>
  );
}

/** Italic emphasis inside a serif Headline — the one typographic flourish. */
export function Em({ children }: { children: React.ReactNode }) {
  return <em className="italic">{children}</em>;
}

export function Subhead({
  children,
  className = "",
  size = "default",
}: {
  children: React.ReactNode;
  className?: string;
  size?: "default" | "large";
}) {
  const sizeClass = size === "large" ? "text-lg md:text-xl" : "text-base md:text-lg";
  return <p className={`${sizeClass} leading-relaxed opacity-75 ${className}`}>{children}</p>;
}

// ──────────────────────────────────────────────────────────────────
// Buttons — ink-bordered, print-tactile
// ──────────────────────────────────────────────────────────────────

interface ButtonBaseProps {
  to?: string;
  href?: string;
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
  size?: "md" | "lg";
  icon?: React.ReactNode;
  external?: boolean;
}

/** Primary CTA — deep green fill, cream text. */
export function GradientButton({
  to,
  href,
  onClick,
  children,
  className = "",
  size = "md",
  icon,
  external,
}: ButtonBaseProps) {
  const sizeClass = size === "lg" ? "h-12 px-7 text-[0.95rem]" : "h-10 px-5 text-sm";
  const body = (
    <span
      className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-[#17150F] bg-[#1E4D38] font-semibold text-[#F5F1E6] transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] hover:-translate-y-0.5 hover:bg-[#173D2C] hover:shadow-[0_6px_20px_-8px_rgba(30,77,56,0.45)] active:translate-y-0 active:scale-[0.98] ${sizeClass} ${className}`}
    >
      {children}
      {icon}
    </span>
  );
  if (to) return <Link to={to}>{body}</Link>;
  if (href)
    return (
      <a href={href} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
        {body}
      </a>
    );
  return (
    <button onClick={onClick} type="button" className="contents">
      {body}
    </button>
  );
}

/** Secondary action — tinted fill with an ink border. */
export function GhostButton({
  to,
  href,
  onClick,
  children,
  className = "",
  size = "md",
  icon,
  external,
}: ButtonBaseProps) {
  const sizeClass = size === "lg" ? "h-12 px-7 text-[0.95rem]" : "h-10 px-5 text-sm";
  const body = (
    <span
      className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-[#17150F]/80 bg-[#E4EFE4] font-semibold text-[#17150F] transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] hover:-translate-y-0.5 hover:bg-[#D6E7D6] hover:shadow-[0_6px_20px_-10px_rgba(30,77,56,0.35)] active:translate-y-0 active:scale-[0.98] ${sizeClass} ${className}`}
    >
      {children}
      {icon}
    </span>
  );
  if (to) return <Link to={to}>{body}</Link>;
  if (href)
    return (
      <a href={href} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
        {body}
      </a>
    );
  return (
    <button onClick={onClick} type="button" className="contents">
      {body}
    </button>
  );
}

// ──────────────────────────────────────────────────────────────────
// GlowCard (name kept for call sites) — a plain paper card
// ──────────────────────────────────────────────────────────────────

export function GlowCard({
  children,
  className = "",
  icon,
  title,
  as: Tag = "div",
}: {
  children?: React.ReactNode;
  className?: string;
  icon?: React.ReactNode;
  title?: React.ReactNode;
  as?: React.ElementType;
}) {
  const Component = Tag as React.ElementType;
  return (
    <Component
      className={`rounded-2xl border border-[#E4DFD3] bg-white p-6 transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] hover:-translate-y-0.5 hover:shadow-[0_14px_36px_-20px_rgba(30,77,56,0.25)] ${className}`}
    >
      {icon && <div className="mb-4 inline-flex text-[#1E4D38]">{icon}</div>}
      {title && <h3 className="mb-2 text-base font-semibold">{title}</h3>}
      {children}
    </Component>
  );
}

// ──────────────────────────────────────────────────────────────────
// Pill — small chips; tones map to states
// ──────────────────────────────────────────────────────────────────

export function Pill({
  children,
  tone = "default",
  className = "",
}: {
  children: React.ReactNode;
  tone?: "default" | "accent" | "warning" | "success";
  className?: string;
}) {
  const toneClass = {
    default: "border-[#E4DFD3] bg-white text-[#17150F]/80",
    accent:  "border-[#1E4D38]/30 bg-[#E4EFE4] text-[#1E4D38]",
    warning: "border-amber-600/30 bg-amber-50 text-amber-800",
    success: "border-emerald-700/30 bg-emerald-50 text-emerald-800",
  }[tone];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${toneClass} ${className}`}
    >
      {children}
    </span>
  );
}
